import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, RefreshCw,Calendar, Shuffle } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { supabase } from "../supabaseClient";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

const FX_PAIRS = ["usd", "eur", "gbp", "jpy", "aud", "cad", "chf", "nzd"];

const PAIR_LABELS = {
  "usd": "USD/INR", "eur": "EUR/INR", "gbp": "GBP/INR",
  "jpy": "JPY/INR", "aud": "AUD/INR", "cad": "CAD/INR",
  "chf": "CHF/INR", "nzd": "NZD/INR",
  "eurusd": "EUR/USD", "gbpusd": "GBP/USD", "usdjpy": "USD/JPY",
  "usdchf": "USD/CHF", "audusd": "AUD/USD", "usdcad": "USD/CAD",
  "nzdusd": "NZD/USD", "eurgbp": "EUR/GBP"
};

const DONUT_COLORS = ["#6366f1", "#22d3ee", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];

const CURRENCY_SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥",
  AUD: "A$", CAD: "C$", CHF: "CHF ", NZD: "NZ$",
  SGD: "S$", AED: "AED ", SAR: "SAR "
};

function fromISO(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  return diff;
}

function isWithinDays(isoDate, days) {
  const d = daysUntil(isoDate);
  return d !== null && d >= 0 && d <= days;
}

function isWithinYear(isoDate) {
  const d = daysUntil(isoDate);
  return d !== null && d >= 0 && d <= 365;
}

export default function MarketIntelligence({ onBack, companyId, onRatesUpdate }) {
  const [hedgeData, setHedgeData]           = useState([]);
  const [receivables, setReceivables]       = useState([]);
  const [payables, setPayables]             = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [fxRates, setFxRates]               = useState([]);
  const [prevRates, setPrevRates]           = useState({});
  const [loadingRates, setLoadingRates]     = useState(true);
  const [loadingData, setLoadingData]       = useState(true);
  const [lastUpdated, setLastUpdated]       = useState(null);
  const [refreshing, setRefreshing]         = useState(false);
  const [eodRates, setEodRates]             = useState({});  // { USD: 84.12, EUR: 91.50, ... }
const [strengthLastUpdated, setStrengthLastUpdated] = useState(null);
  const [lcRecords, setLcRecords] = useState([]);
const [bcRecords, setBcRecords] = useState([]);

  // ── Natural Hedge horizon selector ──
  const [naturalHedgeDays, setNaturalHedgeDays] = useState(7);

  const loadAllData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [hedgeRes, recRes, payRes, profileRes, lcRes, bcRes] = await Promise.all([
  supabase.from("hedge_positions").select("*").eq("company_id", companyId).order("created_at", { ascending: false }),
  supabase.from("receivables").select("*").eq("company_id", companyId).order("due_date", { ascending: true }),
  supabase.from("payables").select("*").eq("company_id", companyId).order("due_date", { ascending: true }),
  supabase.from("company_profiles").select("*").eq("company_id", companyId).limit(1).single(),
  supabase.from("import_financing").select("*").eq("company_id", companyId).order("maturity_date", { ascending: true }),
  supabase.from("import_financing_bc").select("*").eq("company_id", companyId).order("repayment_date", { ascending: true }),
]);

if (Array.isArray(hedgeRes.data))   setHedgeData(hedgeRes.data);
if (Array.isArray(recRes.data))     setReceivables(recRes.data);
if (Array.isArray(payRes.data))     setPayables(payRes.data);
if (profileRes.data)                setCompanyProfile(profileRes.data);
if (Array.isArray(lcRes.data))      setLcRecords(lcRes.data);
if (Array.isArray(bcRes.data))      setBcRecords(bcRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingData(false);
    }
  }, []);

  

// ── Load yesterday's EOD snapshot as baseline for strength index ──
const loadEODRates = useCallback(async () => {
  const { data, error } = await supabase
    .from("fx_rates_eod")
    .select("currency_pair, rate, snapshot_date");

  if (!error && data?.length > 0) {
    const map = {};
    data.forEach(r => { map[r.currency_pair] = parseFloat(r.rate); });
    setEodRates(map);
  }
}, []);

// ── Save EOD snapshot (once per day, only if not already saved) ──
const saveEODSnapshot = useCallback(async (liveRatesMap) => {
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const istHour = ist.getHours();
  const istMinute = ist.getMinutes();

  // Only save EOD snapshot after 17:30 IST (market close)
  if (istHour < 17 || (istHour === 17 && istMinute < 30)) return;

  const today = new Date().toISOString().split("T")[0];

  const rows = Object.entries(liveRatesMap)
    .filter(([, rate]) => rate > 0)
    .map(([currency_pair, rate]) => ({
      currency_pair,
      rate,
      snapshot_date: today,
      created_at: new Date().toISOString()
    }));

  if (rows.length > 0) {
    await supabase
      .from("fx_rates_eod")
      .upsert(rows, { onConflict: "currency_pair" });
  }
}, []);
useEffect(() => {
  loadAllData();
}, [loadAllData]);

useEffect(() => {
  loadEODRates();
}, []);

const fetchRates = useCallback(async () => {
  setRefreshing(true);
  try {
    let inrRates = {};

    try {
      // Primary — freeforexapi
      const pairs = "USDINR,EURINR,GBPINR,JPYINR,AUDINR,CADINR,CHFINR,NZDINR";
      const res = await fetch(`https://www.freeforexapi.com/api/live?pairs=${pairs}`);
      const data = await res.json();

      if (data?.code === 200 && data?.rates) {
        inrRates["usd"] = data.rates["USDINR"]?.rate ? 1 / data.rates["USDINR"].rate : null;
        inrRates["eur"] = data.rates["EURINR"]?.rate ? 1 / data.rates["EURINR"].rate : null;
        inrRates["gbp"] = data.rates["GBPINR"]?.rate ? 1 / data.rates["GBPINR"].rate : null;
        inrRates["jpy"] = data.rates["JPYINR"]?.rate ? 1 / data.rates["JPYINR"].rate : null;
        inrRates["aud"] = data.rates["AUDINR"]?.rate ? 1 / data.rates["AUDINR"].rate : null;
        inrRates["cad"] = data.rates["CADINR"]?.rate ? 1 / data.rates["CADINR"].rate : null;
        inrRates["chf"] = data.rates["CHFINR"]?.rate ? 1 / data.rates["CHFINR"].rate : null;
        inrRates["nzd"] = data.rates["NZDINR"]?.rate ? 1 / data.rates["NZDINR"].rate : null;
      } else {
        throw new Error("freeforexapi failed");
      }
    } catch (e) {
      // Fallback — jsdelivr
      console.warn("Primary FX API failed, using fallback:", e.message);
      const res2 = await fetch(
        "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/inr.json"
      );
      const data2 = await res2.json();
      inrRates = data2?.inr || {};

    }
      const rows = FX_PAIRS.map(pair => {
        const rateVsInr = inrRates[pair];
        const inrPerUnit = rateVsInr ? (1 / rateVsInr) : null;
        const spread = inrPerUnit ? +(inrPerUnit * 0.001).toFixed(4) : null;
        const bid = inrPerUnit ? +(inrPerUnit - spread / 2).toFixed(4) : null;
        const ask = inrPerUnit ? +(inrPerUnit + spread / 2).toFixed(4) : null;
        return {
          pair: PAIR_LABELS[pair] || pair.toUpperCase(),
          key: pair,
          rate: inrPerUnit ? +inrPerUnit.toFixed(4) : null,
          bid, ask,
          spread: spread ? +spread.toFixed(4) : null,
        };
      });

      const g7Pairs = [
        { key: "eurusd", base: "eur", quote: "usd" },
        { key: "gbpusd", base: "gbp", quote: "usd" },
        { key: "usdjpy", base: "usd", quote: "jpy", invert: true },
        { key: "usdchf", base: "usd", quote: "chf", invert: true },
        { key: "audusd", base: "aud", quote: "usd" },
        { key: "usdcad", base: "usd", quote: "cad", invert: true },
        { key: "nzdusd", base: "nzd", quote: "usd" },
        { key: "eurgbp", base: "eur", quote: "gbp" },
      ];

      const g7Rows = g7Pairs.map(({ key, base, quote, invert }) => {
        const baseInr  = inrRates[base]  ? 1 / inrRates[base]  : null;
        const quoteInr = inrRates[quote] ? 1 / inrRates[quote] : null;
        let rate = null;
        if (baseInr && quoteInr) {
          rate = invert ? +(quoteInr / baseInr).toFixed(5) : +(baseInr / quoteInr).toFixed(5);
        }
        const spread = rate ? +(rate * 0.0002).toFixed(5) : null;
        return {
          pair: PAIR_LABELS[key], key, rate,
          bid: rate ? +(rate - spread / 2).toFixed(5) : null,
          ask: rate ? +(rate + spread / 2).toFixed(5) : null,
          spread,
        };
      });

      setPrevRates(prev => {
        const newPrev = {};
        [...rows, ...g7Rows].forEach(r => { if (r.rate) newPrev[r.key] = r.rate; });
        return { ...prev, ...newPrev };
      });

      const allRates = [...rows, ...g7Rows];
     
      setFxRates(allRates);
setLastUpdated(new Date());

// ── Build a clean map for EOD snapshot and strength index ──
const liveRatesMap = {};
["usd","eur","gbp","jpy","aud","chf","cad","nzd"].forEach(key => {
  const r = allRates.find(x => x.key === key);
  if (r?.rate) liveRatesMap[key.toUpperCase()] = r.rate;
});

// Save EOD snapshot if not yet done today (fire-and-forget)
saveEODSnapshot(liveRatesMap);

// Update strength timestamp
setStrengthLastUpdated(new Date());

   if (onRatesUpdate) {
  const find = (pair) => allRates.find(r => r.pair === pair)?.rate || null;
  const usdinr = find("USD/INR");
  const eurinr = find("EUR/INR");
  const gbpinr = find("GBP/INR");
  const sgdinr = allRates.find(r => r.key === "sgd")?.rate || null;
  const jpyinr = allRates.find(r => r.key === "jpy")?.rate || null;
  const aedinr = usdinr ? +(usdinr * 0.2723).toFixed(4) : null; // AED pegged ~3.67/USD

  onRatesUpdate({ usdinr, eurinr, gbpinr });

  // ── Upsert live rates to Supabase so all modules stay current ──
  const upsertRates = [
    usdinr && { currency_pair: 'USDINR', rate: usdinr, updated_at: new Date().toISOString() },
    eurinr && { currency_pair: 'EURINR', rate: eurinr, updated_at: new Date().toISOString() },
    gbpinr && { currency_pair: 'GBPINR', rate: gbpinr, updated_at: new Date().toISOString() },
    sgdinr && { currency_pair: 'SGDINR', rate: sgdinr, updated_at: new Date().toISOString() },
    jpyinr && { currency_pair: 'JPYINR', rate: jpyinr, updated_at: new Date().toISOString() },
    aedinr && { currency_pair: 'AEDINR', rate: aedinr, updated_at: new Date().toISOString() },
  ].filter(Boolean);

  supabase.from('fx_rates').upsert(upsertRates, { onConflict: 'currency_pair' });
  // fire-and-forget — no await, doesn't block UI
}
    } catch (e) {
      console.error("FX fetch error:", e);
    } finally {
      setLoadingRates(false);
      setRefreshing(false);
    }
  }, []);

useEffect(() => {
  fetchRates();
  // Live rate table: every 30 seconds (visible ticks)
  // Strength index: piggybacked on same fetch, no separate timer needed
  const interval = setInterval(fetchRates, 30 * 60 * 1000); // 30 minutes
  return () => clearInterval(interval);
}, [fetchRates]);

  // ── Helper: parse amount from text ──
  const parseAmt = (val) => parseFloat((val || "").toString().replace(/[^0-9.]/g, "")) || 0;

  // ── Total FX Exposure for the year ──
  const recExposureYear = receivables
    .filter(r => isWithinYear(r.due_date) && r.status !== "Settled")
    .reduce((sum, r) => {
      const total = parseAmt(r.amount);
      const settled = parseFloat(r.settled_amount) || 0;
      return sum + Math.max(0, total - settled);
    }, 0);

  const payExposureYear = payables
    .filter(p => isWithinYear(p.due_date) && p.status !== "Settled")
    .reduce((sum, p) => {
      const total = parseAmt(p.amount);
      const settled = parseFloat(p.settled_amount) || 0;
      return sum + Math.max(0, total - settled);
    }, 0);

  const debtExposureYear = (companyProfile?.debt_profile || [])
    .filter(d => isWithinYear(d.maturityDate || d.maturity_date))
    .reduce((sum, d) => sum + parseAmt(d.loanAmount), 0);

 const lcExposureYear = lcRecords
  .filter(r => {
    const effDate = r.lc_type === 'Usance' && r.usance_period_days
      ? (() => { const d = new Date(r.maturity_date); d.setDate(d.getDate() + parseInt(r.usance_period_days)); return d.toISOString().split('T')[0]; })()
      : r.maturity_date;
    return r.status !== 'Settled' && r.status !== 'Converted to BC' && isWithinYear(effDate);
  })
  .reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0), 0);

const bcExposureYear = bcRecords
  .filter(r => r.status !== 'Settled' && isWithinYear(r.repayment_date))
  .reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0), 0);

const totalExposureYear = recExposureYear + payExposureYear + debtExposureYear + lcExposureYear + bcExposureYear;

  // ── Hedged exposure ──
  const totalHedged = hedgeData
  .filter(h => !['Settled', 'Cancelled'].includes(h.status || 'Active'))
  .reduce((sum, h) => sum + (parseFloat(h.notional_amount_num) || 0), 0);

  // ── Unhedged exposure ──
  const totalUnhedged = Math.max(0, totalExposureYear - totalHedged);

  // ── Hedge % ──
  const hedgeRatio = totalExposureYear > 0 ? ((totalHedged / totalExposureYear) * 100).toFixed(1) : 0;

  // ── Exposure by currency (from hedge positions + receivables/payables) ──
  const exposureData = (() => {
    const map = {};
    hedgeData.forEach(h => {
      const pair = h.currency_pair || "";
      const base = pair.split("/")[0];
      const amt  = parseFloat(h.notional_amount_num) || 0;
      if (!base || amt === 0) return;
      if (!map[base]) map[base] = { currency: base, hedged: 0, unhedged: 0, naturallyHedged: 0 };
      map[base].hedged += amt;
    });
    [...receivables, ...payables].forEach(item => {
      const ccy = item.currency;
      if (!ccy || ccy === "INR") return;
      const amt = parseAmt(item.amount);
      if (!map[ccy]) map[ccy] = { currency: ccy, hedged: 0, unhedged: 0, naturallyHedged: 0 };
      map[ccy].unhedged += amt;
    });
    Object.values(map).forEach(d => {
      d.unhedged = Math.max(0, d.unhedged - d.hedged);
    });
    return Object.values(map).filter(d => d.hedged > 0 || d.unhedged > 0);
  })();

  function getEffLCDate(r) {
  if (r.lc_type === 'Usance' && r.usance_period_days) {
    const d = new Date(r.maturity_date + 'T00:00:00');
    d.setDate(d.getDate() + parseInt(r.usance_period_days));
    return d.toISOString().split('T')[0];
  }
  return r.maturity_date;
}

  // ── Natural Hedge with proximity matching ──
  // Horizon = naturalHedgeDays (how far out we look)
  // Proximity window = always 7 days (rec/pay within 7d of each other count as naturally hedged)
const computeNaturalHedge = useCallback((horizonDays) => {
    const PROXIMITY_DAYS = 7;

    // Receivables — money coming IN (reduces payable exposure)
    const recs = [
      // Trade receivables
      ...receivables
        .filter(r => isWithinDays(r.due_date, horizonDays) && r.currency && r.currency !== "INR" && r.status !== "Settled")
        .map(r => {
          const total   = parseAmt(r.amount);
          const settled = parseFloat(r.settled_amount) || 0;
          return { currency: r.currency, amount: Math.max(0, total - settled), date: r.due_date, source: 'Receivable' };
        }),
    ];

    // Payables — money going OUT (LC, BC, trade payables.lcRecords, bcRecords)
    const pays = [
      // Trade payables
      ...payables
        .filter(p => isWithinDays(p.due_date, horizonDays) && p.currency && p.currency !== "INR" && p.status !== "Settled")
        .map(p => {
          const total   = parseAmt(p.amount);
          const settled = parseFloat(p.settled_amount) || 0;
          return { currency: p.currency, amount: Math.max(0, total - settled), date: p.due_date, source: 'Payable' };
        }),
      // LC payments (import payables)
    ...lcRecords
  .filter(r => isWithinDays(getEffLCDate(r), horizonDays) && r.currency && r.currency !== 'INR' && r.status !== 'Settled' && r.status !== 'Converted to BC')
  .map(r => ({ currency: r.currency, amount: parseFloat(r.amount_fcy) || 0, date: getEffLCDate(r), source: 'LC' })),
      // BC repayments (import payables)
      ...bcRecords
        .filter(b => isWithinDays(b.repayment_date, horizonDays) && b.currency && b.currency !== "INR" && b.status !== 'Settled')
        .map(b => ({ currency: b.currency, amount: parseFloat(b.amount_fcy) || 0, date: b.repayment_date, source: 'BC' })),
    ];

    const map = {};
    [...recs, ...pays].forEach(item => {
      if (!map[item.currency]) map[item.currency] = { currency: item.currency, receivables: 0, payables: 0, naturallyHedged: 0 };
    });

    Object.keys(map).forEach(ccy => {
  const ccyRecs      = recs.filter(r => r.currency === ccy);
  const ccyTradePays = pays.filter(p => p.currency === ccy && p.source === 'Payable');
  const ccyAllPays   = pays.filter(p => p.currency === ccy);

  map[ccy].receivables     = ccyRecs.reduce((s, r) => s + r.amount, 0);
  map[ccy].payables        = ccyAllPays.reduce((s, p) => s + p.amount, 0);
  map[ccy].naturallyHedged = Math.min(
    map[ccy].receivables,
    ccyTradePays.reduce((s, p) => s + p.amount, 0)
  );
});

    return Object.values(map)
      .filter(d => d.receivables > 0 || d.payables > 0)
      .map(d => ({ ...d, net: d.receivables - d.payables }));
  }, [receivables, payables]);

  const naturalHedgeData = computeNaturalHedge(naturalHedgeDays);

  // Total natural hedge across all currencies (used in bar chart)
  const totalNaturalHedged = naturalHedgeData.reduce((s, d) => s + d.naturallyHedged, 0);

  // Merge naturallyHedged per currency into exposureData for bar chart
  const exposureDataWithNH = exposureData.map(d => {
    const nhRow = naturalHedgeData.find(n => n.currency === d.currency);
    return { ...d, naturallyHedged: nhRow ? nhRow.naturallyHedged : 0 };
  });

  // ── Upcoming maturities ──
  const upcomingMaturities = hedgeData
    .map(h => ({ ...h, maturityDate: h.maturity_date || h.expiry_date, days: daysUntil(h.maturity_date || h.expiry_date) }))
    .filter(h => h.maturityDate && h.days !== null && h.days >= 0)
    .sort((a, b) => a.days - b.days)
    .slice(0, 20);

  const formatAmount = (n, currency = null) => {
    if (!n && n !== 0) return "0";
    const symbol = currency ? (CURRENCY_SYMBOLS[currency] || currency + " ") : "";
    if (Math.abs(n) >= 10000000) return `${symbol}${(n / 10000000).toFixed(2)}Cr`;
    if (Math.abs(n) >= 100000)   return `${symbol}${(n / 100000).toFixed(2)}L`;
    if (Math.abs(n) >= 1000)     return `${symbol}${(n / 1000).toFixed(2)}K`;
    return `${symbol}${n.toFixed(0)}`;
  };

  const urgencyColor = (days) => {
    if (days <= 7)  return "bg-red-500/20 text-red-400 border-red-500/30";
    if (days <= 30) return "bg-amber-500/20 text-amber-400 border-amber-500/30";
    return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  };

  const riskRows = [
    { move: "+1%", impact: totalUnhedged * 0.01, level: "LOW",    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
    { move: "+3%", impact: totalUnhedged * 0.03, level: "MEDIUM", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
    { move: "+5%", impact: totalUnhedged * 0.05, level: "HIGH",   color: "bg-red-500/20 text-red-400 border-red-500/30" },
  ];

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Real-Time Market Intelligence</h2>
          <p className="text-gray-400 text-sm">Live FX rates · Currency exposure · Risk simulation</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {lastUpdated && <span className="text-xs text-gray-500">Updated: {lastUpdated.toLocaleTimeString()}</span>}
          <button
            onClick={() => { fetchRates(); loadAllData(); }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── TOP KPI STRIP ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total FX Exposure (Year)</p>
          <p className="text-2xl font-bold text-white">{formatAmount(totalExposureYear)}</p>
          <p className="text-xs text-gray-500 mt-1">Rec + Pay + LC + BC + Debt due 12m</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Hedged</p>
          <p className="text-2xl font-bold text-emerald-400">{formatAmount(totalHedged)}</p>
          <p className="text-xs text-gray-500 mt-1">{hedgeRatio}% of total exposure</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Unhedged</p>
          <p className="text-2xl font-bold text-red-400">{formatAmount(totalUnhedged)}</p>
          <p className="text-xs text-gray-500 mt-1">{(100 - parseFloat(hedgeRatio)).toFixed(1)}% of total exposure</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Active Positions</p>
          <p className="text-2xl font-bold text-indigo-400">
  {hedgeData.filter(h => !['Settled', 'Cancelled'].includes(h.status || 'Active')).length}
</p>
          <p className="text-xs text-gray-500 mt-1">Hedge instruments</p>
        </div>
      </div>

      {/* ── MAIN ROW: Exposure + Right Column ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* Left — Currency Exposure */}
        <div className="nebula-card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Currency Exposure Overview</h3>
          {exposureData.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              <p>No exposure data found.</p>
              <p className="mt-1">Add hedge positions, receivables and payables to see charts.</p>
            </div>
          ) : (
            <>
              {/* Large Donut */}
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Exposure by Currency</p>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={exposureData}
                    dataKey="hedged"
                    nameKey="currency"
                    cx="50%" cy="50%"
                    innerRadius={75} outerRadius={115}
                    paddingAngle={3}
                    label={({ currency, percent }) => `${currency} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: "#4b5563", strokeWidth: 1 }}
                  >
                    {exposureData.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val, name) => [formatAmount(val, name), name]}
                    contentStyle={{ background: "#12141a", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Currency pills */}
              <div className="flex flex-wrap gap-2 justify-center mb-5">
                {exposureData.map((d, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1 rounded-full border"
                    style={{ borderColor: DONUT_COLORS[i % DONUT_COLORS.length] + "40", background: DONUT_COLORS[i % DONUT_COLORS.length] + "15" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="text-xs font-medium" style={{ color: DONUT_COLORS[i % DONUT_COLORS.length] }}>{d.currency}</span>
                    <span className="text-xs text-gray-400">{formatAmount(d.hedged, d.currency)}</span>
                  </div>
                ))}
              </div>

              {/* Bar chart — Hedged / Unhedged / Natural Hedge */}
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">
                Hedged vs Unhedged vs Natural Hedge by Currency
                <span className="ml-2 text-gray-600 normal-case">(NH horizon: {naturalHedgeDays}d)</span>
              </p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={exposureDataWithNH} barSize={14} barGap={3}>
                  <XAxis dataKey="currency" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#2a2d3a" }} tickLine={false} />
                  <YAxis hide={false} tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatAmount(v)} width={60} />
                  <Tooltip
                    formatter={(val, name) => [formatAmount(val), name]}
                    contentStyle={{ background: "#12141a", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }}
                  />
                  <Bar dataKey="hedged"          name="Hedged"        fill="#6366f1" radius={[4,4,0,0]} />
                  <Bar dataKey="unhedged"         name="Unhedged"      fill="#ef4444" radius={[4,4,0,0]} />
                  <Bar dataKey="naturallyHedged"  name="Natural Hedge" fill="#14b8a6" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-2">
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-indigo-500" /><span className="text-xs text-gray-400">Hedged</span></div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-red-500" /><span className="text-xs text-gray-400">Unhedged</span></div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-teal-400" /><span className="text-xs text-gray-400">Natural Hedge</span></div>
              </div>
            </>
          )}
        

        {/* Currency Strength Index */}
{fxRates.length > 0 && (
  <div className="mt-6">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs text-gray-400 uppercase tracking-wider">Currency Strength Index</p>
      <div className="flex items-center gap-2">
        {Object.keys(eodRates).length === 0 && (
          <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
            No EOD baseline yet — will save on first load
          </span>
        )}
        {strengthLastUpdated && (
          <span className="text-[10px] text-gray-500">
            {strengthLastUpdated.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
    </div>

    {(() => {
      const CURRENCIES = ["USD","EUR","GBP","JPY","AUD","CHF","CAD","NZD"];
      const strengths = CURRENCIES.map(ccy => {
        const key    = ccy.toLowerCase();
        const live   = fxRates.find(r => r.key === key)?.rate || 0;
        const eod    = eodRates[ccy] || 0;
        const pct    = eod > 0 ? ((live - eod) / eod) * 100 : null;
        return { currency: ccy, live, eod, pct };
      }).filter(s => s.live > 0);

      // Sort by % change descending (strongest gainer vs INR first)
      const sorted = [...strengths].sort((a, b) => {
        if (a.pct === null && b.pct === null) return b.live - a.live;
        if (a.pct === null) return 1;
        if (b.pct === null) return -1;
        return b.pct - a.pct;
      });

      const hasEOD = sorted.some(s => s.pct !== null);
      const maxAbs = hasEOD
        ? Math.max(...sorted.filter(s => s.pct !== null).map(s => Math.abs(s.pct)), 0.01)
        : 1;

      return (
        <div className="space-y-2.5">
          {!hasEOD && (
            <p className="text-[11px] text-gray-500 italic mb-2">
              Showing raw rates — % change will appear once EOD baseline is recorded
            </p>
          )}
          {sorted.map((s, i) => {
            const isTop    = i === 0;
            const isBottom = i === sorted.length - 1;
            const up       = s.pct !== null ? s.pct >= 0 : null;
            const barPct   = hasEOD && s.pct !== null
              ? (Math.abs(s.pct) / maxAbs) * 100
              : (s.live / (sorted[0]?.live || 1)) * 100;
            const barColor = s.pct === null
              ? "#6366f1"
              : s.pct > 0 ? "#10b981" : s.pct < 0 ? "#ef4444" : "#6366f1";

            return (
              <div key={s.currency} className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-300 w-8">{s.currency}</span>
                <div className="flex-1 bg-[#1a1d26] rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(barPct, 2)}%`, background: barColor }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-16 text-right tabular-nums">
                  {s.live.toFixed(2)}
                </span>
                <span className={`text-xs font-semibold w-16 text-right tabular-nums ${
                  s.pct === null ? "text-gray-600" :
                  s.pct > 0 ? "text-emerald-400" : s.pct < 0 ? "text-red-400" : "text-gray-400"
                }`}>
                  {s.pct === null ? "—" : `${s.pct > 0 ? "+" : ""}${s.pct.toFixed(3)}%`}
                </span>
                <div className="w-20 text-right">
                  {isTop && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 whitespace-nowrap">
                      ▲ Strongest
                    </span>
                  )}
                  {isBottom && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 whitespace-nowrap">
                      ▼ Weakest
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          <p className="text-[10px] text-gray-600 pt-1">
            % change vs today's open · Positive = currency strengthening vs INR
          </p>
        </div>
      );
    })()}
  </div>
)}
</div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">

          {/* Natural Hedge */}
          <div className="nebula-card p-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Shuffle className="w-5 h-5 text-teal-400" />
              <h3 className="text-lg font-bold text-white">Natural Hedge</h3>
              {/* Horizon toggle buttons */}
              <div className="ml-auto flex items-center gap-1">
                {[7, 15, 30, 60, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setNaturalHedgeDays(d)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all font-medium ${
                      naturalHedgeDays === d
                        ? "bg-teal-500/30 text-teal-300 border-teal-500/50"
                        : "bg-transparent text-gray-500 border-[#2a2d3a] hover:text-teal-400 hover:border-teal-500/30"
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            {naturalHedgeData.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No receivables or payables due in next {naturalHedgeDays} days.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2a2d3a]">
                      {["Currency", "Receivables", "Payables", "Net Position", "Natural Hedge"].map(h => (
                        <th key={h} className="text-left text-xs text-gray-500 uppercase py-2 pr-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {naturalHedgeData.map((d, i) => (
                      <tr key={i} className="border-b border-[#2a2d3a]/40">
                        <td className="py-3 pr-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-400">{d.currency}</span>
                        </td>
                        <td className="py-3 pr-3 text-emerald-400 font-medium">{formatAmount(d.receivables, d.currency)}</td>
                        <td className="py-3 pr-3 text-red-400 font-medium">{formatAmount(d.payables, d.currency)}</td>
                        <td className="py-3 pr-3">
                          <span className={`font-semibold ${d.net >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {d.net >= 0 ? "+" : ""}{formatAmount(d.net, d.currency)}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="text-teal-400 font-medium">{formatAmount(d.naturallyHedged, d.currency)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-3 p-3 rounded-xl bg-teal-500/10 border border-teal-500/20">
              <p className="text-teal-300 text-xs">
                Horizon: <span className="font-semibold">{naturalHedgeDays} days</span> · Proximity window: <span className="font-semibold">7 days</span> — rec/pay pairs within 7 days of each other are counted as naturally hedged, regardless of horizon selected.
              </p>
            </div>
          </div>

          {/* Upcoming Maturities */}
          <div className="nebula-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-indigo-400" />
              <h3 className="text-lg font-bold text-white">Upcoming Contract Maturities</h3>
            </div>
            {upcomingMaturities.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">No upcoming maturities found.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {upcomingMaturities.map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                    <div>
                      <p className="text-sm font-medium text-white">{h.contract_reference}</p>
                      <p className="text-xs text-gray-400">{h.instrument_type} · {h.currency_pair} · {h.direction}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400 mb-1">{fromISO(h.maturityDate)}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${urgencyColor(h.days)}`}>
                        {h.days === 0 ? "Today" : `${h.days}d`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* FX Risk Simulation */}
          <div className="nebula-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-amber-400 text-lg">⚠</span>
              <h3 className="text-lg font-bold text-white">FX Risk Simulation</h3>
            </div>
            <p className="text-xs text-gray-400 mb-4">Potential impact on total annual exposure</p>
            {totalExposureYear === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Add receivables, payables and hedge positions to see risk simulation.</p>
            ) : (
              <>
                <table className="w-full text-sm mb-4">
                  <thead>
                    <tr className="border-b border-[#2a2d3a]">
                      <th className="text-left text-xs text-gray-500 uppercase py-2 pr-4 font-medium">FX Movement</th>
                      <th className="text-left text-xs text-gray-500 uppercase py-2 pr-4 font-medium">Impact</th>
                      <th className="text-left text-xs text-gray-500 uppercase py-2 font-medium">Risk Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {riskRows.map((r, i) => (
                      <tr key={i} className="border-b border-[#2a2d3a]/40">
                        <td className="py-3 pr-4 text-white font-semibold text-lg">{r.move}</td>
                        <td className="py-3 pr-4 text-white font-bold">{formatAmount(r.impact)}</td>
                        <td className="py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold border ${r.color}`}>{r.level}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                  <p className="text-indigo-300 text-xs">
                    Based on unhedged exposure of <span className="font-bold text-red-400">{formatAmount(totalUnhedged)}</span> · Total exposure: {formatAmount(totalExposureYear)}
                  </p>
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Economic Events + FX News */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* Key Economic Events */}
        <div className="nebula-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-indigo-400 text-lg">📅</span>
            <h3 className="text-lg font-bold text-white">Key Economic Events</h3>
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">This Week</span>
          </div>
          <EconomicEventsPanel />
        </div>

        {/* FX News */}
        <FXNewsPanel />

      </div>

{/* FX Rates */}
      <div className="nebula-card overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2d3a]">
          <div>
            <h3 className="text-lg font-bold text-white">FX Rates</h3>
            <p className="text-xs text-gray-400">Refreshes every 30 minutes · Indicative only</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-amber-400/70 italic">Indicative rates · May be delayed · Not for dealing</span>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs text-amber-400">Delayed</span>
            </div>
          </div>
        </div>
        {loadingRates ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#12141a]">
                  {["Currency Pair", "Rate"].map(h => (
                    <th key={h} className="text-left text-xs text-gray-500 uppercase py-3 px-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fxRates.map((r, i) => (
                  <tr key={r.key} className={`border-b border-[#2a2d3a]/40 ${i % 2 === 0 ? "" : "bg-[#12141a]/40"}`}>
                    <td className="py-3 px-4 font-semibold text-white">{r.pair}</td>
                    <td className="py-3 px-4 font-bold text-white">{r.rate ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FXNewsPanel() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          "https://api.rss2json.com/v1/api.json?rss_url=https://www.forexlive.com/feed/news"
        );
        const data = await res.json();
        if (data.items) {
          setNews(data.items.slice(0, 8).map(item => ({
            title: item.title,
            date: new Date(item.pubDate).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
            link: item.link,
            category: item.categories?.[0] || "FX"
          })));
        }
      } catch (e) {
        setNews([
          { title: "Dollar steady as markets await Fed minutes", date: "10:30 AM", category: "USD" },
          { title: "Euro holds gains after ECB commentary", date: "09:45 AM", category: "EUR" },
          { title: "RBI holds rates, INR strengthens slightly", date: "09:00 AM", category: "INR" },
          { title: "GBP supported by stronger UK retail data", date: "08:30 AM", category: "GBP" },
          { title: "Yen weakens as BOJ maintains ultra-loose policy", date: "08:00 AM", category: "JPY" },
          { title: "Gold rises on dollar weakness", date: "07:45 AM", category: "Commodities" },
          { title: "Oil steadies amid OPEC output concerns", date: "07:30 AM", category: "Commodities" },
          { title: "Asian markets mixed on China PMI data", date: "07:00 AM", category: "Asia" },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
    const interval = setInterval(fetchNews, 300000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="nebula-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-amber-400 text-lg">📰</span>
        <h3 className="text-lg font-bold text-white">Live FX News</h3>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
          Refreshes every 5 min
        </span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {news.map(function(n, i) {
            return (
              <div
                key={i}
                onClick={function() { if (n.link) { window.open(n.link, "_blank"); } }}
                className="flex items-start gap-3 p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] hover:border-indigo-500/50 transition-all cursor-pointer group"
              >
                <div className="flex-1">
                  <p className="text-sm text-gray-300 group-hover:text-white transition-colors leading-snug">
                    {n.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">{n.date}</span>
                    {n.category ? (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400">
                        {n.category}
                      </span>
                    ) : null}
                  </div>
                </div>
                <span className="text-gray-600 group-hover:text-indigo-400 text-xs mt-1">→</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── ECONOMIC EVENTS DATA ──────────────────────────────────────────────────────
const ALL_ECONOMIC_EVENTS = [
  // April 2026
  { date: "2026-04-06", time: "14:00 ET", name: "ISM Services PMI",                                   impact: "HIGH",   currency: "USD" },
  { date: "2026-04-07", time: "12:15 ET", name: "ADP Employment Change",                               impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-07", time: "12:30 ET", name: "Durable Goods Orders",                                impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-07", time: "16:35 ET", name: "Fed's Goolsbee Speech",                               impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-08", time: "18:00 ET", name: "FOMC Minutes",                                        impact: "HIGH",   currency: "USD" },
  { date: "2026-04-09", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-09", time: "12:30 ET", name: "Core PCE Price Index (YoY)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-09", time: "12:30 ET", name: "GDP Annualized (Q4)",                                 impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-09", time: "12:30 ET", name: "Initial Jobless Claims",                              impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-09", time: "12:30 ET", name: "Personal Income (MoM)",                               impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-10", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-10", time: "12:30 ET", name: "Consumer Price Index (YoY)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-10", time: "12:30 ET", name: "CPI ex Food & Energy (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-10", time: "14:00 ET", name: "Michigan Consumer Sentiment",                         impact: "HIGH",   currency: "USD" },
  { date: "2026-04-13", time: "00:00 ET", name: "IMF Meeting",                                         impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-14", time: "12:30 ET", name: "Producer Price Index (MoM)",                          impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-14", time: "12:30 ET", name: "PPI ex Food & Energy (YoY)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-16", time: "12:30 ET", name: "Initial Jobless Claims",                              impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-16", time: "12:30 ET", name: "Philadelphia Fed Manufacturing Survey",               impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-16", time: "13:15 ET", name: "Industrial Production (MoM)",                         impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-21", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-04-21", time: "12:30 ET", name: "Retail Sales Control Group",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-23", time: "13:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
  { date: "2026-04-23", time: "13:45 ET", name: "S&P Global Services PMI",                             impact: "HIGH",   currency: "USD" },
  { date: "2026-04-24", time: "14:00 ET", name: "Michigan Consumer Sentiment",                         impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-28", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-28", time: "12:30 ET", name: "Core PCE Price Index (YoY)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-28", time: "14:00 ET", name: "Consumer Confidence",                                 impact: "MEDIUM", currency: "USD" },
  { date: "2026-04-29", time: "18:00 ET", name: "Fed Interest Rate Decision",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-04-29", time: "18:00 ET", name: "Fed Monetary Policy Statement",                       impact: "HIGH",   currency: "USD" },
  { date: "2026-04-29", time: "18:30 ET", name: "FOMC Press Conference",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-04-30", time: "12:30 ET", name: "GDP Annualized (Q1)",                                 impact: "HIGH",   currency: "USD" },
  { date: "2026-04-30", time: "12:30 ET", name: "Initial Jobless Claims",                              impact: "MEDIUM", currency: "USD" },
  // May 2026
  { date: "2026-05-01", time: "14:00 ET", name: "ISM Manufacturing PMI",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-05-05", time: "14:00 ET", name: "ISM Services PMI",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-05-06", time: "12:15 ET", name: "ADP Employment Change",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-05-07", time: "12:30 ET", name: "Initial Jobless Claims",                              impact: "MEDIUM", currency: "USD" },
  { date: "2026-05-08", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-05-08", time: "12:30 ET", name: "Average Hourly Earnings (MoM)",                       impact: "HIGH",   currency: "USD" },
  { date: "2026-05-12", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-05-12", time: "12:30 ET", name: "CPI ex Food & Energy (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-05-13", time: "12:30 ET", name: "PPI ex Food & Energy (YoY)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-05-15", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-05-15", time: "14:00 ET", name: "Michigan Consumer Sentiment",                         impact: "HIGH",   currency: "USD" },
  { date: "2026-05-20", time: "19:00 ET", name: "FOMC Minutes",                                        impact: "HIGH",   currency: "USD" },
  { date: "2026-05-21", time: "13:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
  { date: "2026-05-21", time: "13:45 ET", name: "S&P Global Services PMI",                             impact: "HIGH",   currency: "USD" },
  { date: "2026-05-29", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  // June 2026
  { date: "2026-06-03", time: "12:15 ET", name: "ADP Employment Change",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-06-03", time: "14:00 ET", name: "ISM Services PMI",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-06-05", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-06-10", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-06-10", time: "12:30 ET", name: "CPI ex Food & Energy (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-06-16", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-06-17", time: "18:00 ET", name: "Fed Interest Rate Decision",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-06-17", time: "18:00 ET", name: "FOMC Economic Projections",                           impact: "HIGH",   currency: "USD" },
  { date: "2026-06-17", time: "18:30 ET", name: "FOMC Press Conference",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-06-23", time: "13:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
  { date: "2026-06-23", time: "13:45 ET", name: "S&P Global Services PMI",                             impact: "HIGH",   currency: "USD" },
  { date: "2026-06-26", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  // July 2026
  { date: "2026-07-01", time: "14:00 ET", name: "ISM Manufacturing PMI",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-07-02", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-07-08", time: "18:00 ET", name: "FOMC Minutes",                                        impact: "HIGH",   currency: "USD" },
  { date: "2026-07-14", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-07-16", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-07-29", time: "18:00 ET", name: "Fed Interest Rate Decision",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-07-29", time: "18:30 ET", name: "FOMC Press Conference",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-07-30", time: "12:30 ET", name: "GDP Annualized (Q2)",                                 impact: "HIGH",   currency: "USD" },
  // Aug 2026
  { date: "2026-08-05", time: "12:15 ET", name: "ADP Employment Change",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-08-07", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-08-12", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-08-17", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-08-19", time: "19:00 ET", name: "FOMC Minutes",                                        impact: "HIGH",   currency: "USD" },
  { date: "2026-08-21", time: "13:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
  { date: "2026-08-28", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  // Sep 2026
  { date: "2026-09-02", time: "12:15 ET", name: "ADP Employment Change",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-09-04", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-09-11", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-09-16", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-09-16", time: "18:00 ET", name: "Fed Interest Rate Decision",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-09-16", time: "18:00 ET", name: "FOMC Economic Projections",                           impact: "HIGH",   currency: "USD" },
  { date: "2026-09-16", time: "18:30 ET", name: "FOMC Press Conference",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-09-23", time: "13:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
  { date: "2026-09-30", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  // Oct 2026
  { date: "2026-10-01", time: "14:00 ET", name: "ISM Manufacturing PMI",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-10-02", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-10-07", time: "18:00 ET", name: "FOMC Minutes",                                        impact: "HIGH",   currency: "USD" },
  { date: "2026-10-14", time: "12:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-10-16", time: "12:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-10-28", time: "18:00 ET", name: "Fed Interest Rate Decision",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-10-28", time: "18:30 ET", name: "FOMC Press Conference",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-10-29", time: "12:30 ET", name: "GDP Annualized (Q3)",                                 impact: "HIGH",   currency: "USD" },
  { date: "2026-10-30", time: "12:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  // Nov 2026
  { date: "2026-11-04", time: "12:15 ET", name: "ADP Employment Change",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-11-06", time: "12:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-11-10", time: "13:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-11-17", time: "13:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-11-18", time: "19:00 ET", name: "FOMC Minutes",                                        impact: "HIGH",   currency: "USD" },
  { date: "2026-11-23", time: "14:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
  { date: "2026-11-27", time: "13:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  // Dec 2026
  { date: "2026-12-02", time: "13:15 ET", name: "ADP Employment Change",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-12-03", time: "15:00 ET", name: "ISM Services PMI",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-12-04", time: "13:30 ET", name: "Nonfarm Payrolls",                                    impact: "HIGH",   currency: "USD" },
  { date: "2026-12-04", time: "15:00 ET", name: "Michigan Consumer Sentiment",                         impact: "HIGH",   currency: "USD" },
  { date: "2026-12-10", time: "13:30 ET", name: "Consumer Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-12-09", time: "19:00 ET", name: "Fed Interest Rate Decision",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-12-09", time: "19:00 ET", name: "FOMC Economic Projections",                           impact: "HIGH",   currency: "USD" },
  { date: "2026-12-09", time: "19:30 ET", name: "FOMC Press Conference",                               impact: "HIGH",   currency: "USD" },
  { date: "2026-12-16", time: "13:30 ET", name: "Retail Sales (MoM)",                                  impact: "HIGH",   currency: "USD" },
  { date: "2026-12-18", time: "13:30 ET", name: "Core PCE Price Index (MoM)",                          impact: "HIGH",   currency: "USD" },
  { date: "2026-12-18", time: "14:45 ET", name: "S&P Global Manufacturing PMI",                        impact: "HIGH",   currency: "USD" },
];

function EconomicEventsPanel() {
  const today0   = new Date(); today0.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today0); tomorrow.setDate(today0.getDate() + 1);
  const nextWeek = new Date(today0); nextWeek.setDate(today0.getDate() + 7);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months   = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const events = ALL_ECONOMIC_EVENTS
    .filter(e => {
      const d = new Date(e.date + "T00:00:00");
      return d >= today0 && d <= nextWeek;
    })
    .map(e => {
      const eventDate = new Date(e.date + "T00:00:00");
      let displayDate;
      if (eventDate.getTime() === today0.getTime())         displayDate = "Today";
      else if (eventDate.getTime() === tomorrow.getTime())  displayDate = "Tomorrow";
      else displayDate = dayNames[eventDate.getDay()];
      return {
        ...e,
        displayDate,
        fullDate: `${eventDate.getDate()} ${months[eventDate.getMonth()]}`,
      };
    })
    .slice(0, 6);

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        No HIGH or MEDIUM impact events this week.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {events.map((e, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
          <div className="text-center min-w-[52px]">
            <p className="text-xs font-bold text-indigo-400">{e.displayDate}</p>
            <p className="text-[10px] text-gray-600">{e.fullDate}</p>
            <p className="text-[10px] text-gray-600 mt-0.5">{e.time}</p>
          </div>
          <div className="flex-1">
            <p className="text-sm text-white font-medium leading-snug">{e.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400">{e.currency}</span>
            </div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border whitespace-nowrap mt-0.5 ${
            e.impact === "HIGH"
              ? "bg-red-500/20 text-red-400 border-red-500/30"
              : "bg-amber-500/20 text-amber-400 border-amber-500/30"
          }`}>
            {e.impact}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-gray-600 text-center pt-1">
        HIGH & MEDIUM impact only · Times in ET · Source: Economic Calendar 2026
      </p>
    </div>
  );
}
